import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, ShieldAlert, ThumbsUp, MapPin, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import PageHero from "@/components/PageHero";

const CATEGORIES = ["theft", "harassment", "accident", "suspicious", "hazard", "missing"];
const SEVERITY = ["low", "medium", "high"];
const SEV_COLOR = { low: "secondary", medium: "accent", high: "destructive" };

export default function Community() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "suspicious", severity: "medium" });

  const load = async () => {
    const url = filter === "all" ? "/community" : `/community?category=${filter}`;
    const { data } = await api.get(url);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    if (!form.title || !form.description) return toast.error("Title and description required");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          await api.post("/community", { ...form, latitude: p.coords.latitude, longitude: p.coords.longitude });
          toast.success("Alert reported. Thank you for keeping the community safe.");
          setForm({ title: "", description: "", category: "suspicious", severity: "medium" });
          setOpen(false);
          load();
        } catch { toast.error("Failed"); }
      },
      async () => {
        // Fallback location
        try {
          await api.post("/community", { ...form, latitude: 12.9716, longitude: 77.5946 });
          toast.success("Alert reported");
          setOpen(false);
          load();
        } catch { toast.error("Failed"); }
      }
    );
  };

  const upvote = async (id) => {
    await api.post(`/community/${id}/upvote`);
    load();
  };

  const points = items.map((a) => ({ lat: a.latitude, lng: a.longitude, label: a.title, color: SEV_COLOR[a.severity] || "primary" }));

  return (
    <div className="max-w-6xl">
      <PageHero
        kicker="Community"
        title="Neighborhood safety feed"
        subtitle="Anonymous reporting. Verified alerts. Real-time heatmap."
        image="https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
        accent="destructive"
        testId="community-hero"
      />
      <div className="flex justify-end mb-6 flex-wrap gap-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="report-alert-btn" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Report incident
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Report a community incident</DialogTitle>
              <DialogDescription>Your location is attached anonymously. Reports appear on the neighborhood heatmap.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input data-testid="community-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Chain snatching near park" /></div>
              <div><Label>Description</Label><Textarea data-testid="community-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{SEVERITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="submit-alert-btn" onClick={submit} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">Broadcast to community</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl overflow-hidden border border-border/70 mb-6" style={{ height: 340 }}>
        {points.length > 0 ? <LiveMap points={points} zoom={11} showCircles /> : (
          <div className="w-full h-full grid place-items-center bg-muted text-sm text-muted-foreground">No incidents reported yet. The map lights up as reports arrive.</div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {["all", ...CATEGORIES].map((c) => (
          <Button key={c} data-testid={`filter-${c}`} variant={filter === c ? "default" : "outline"} size="sm" onClick={() => setFilter(c)} className={filter === c ? "bg-foreground text-background" : ""}>
            {c}
          </Button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-medium">No incidents in this category.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="rounded-2xl border border-border/70 bg-card p-5" data-testid={`community-item-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[hsl(var(--destructive))]/10 grid place-items-center shrink-0">
                  <AlertTriangle className={`w-4 h-4 text-[hsl(var(--${SEV_COLOR[a.severity]}))]`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{a.category}</Badge>
                    <Badge className={`uppercase text-[10px] tracking-wider bg-[hsl(var(--${SEV_COLOR[a.severity]}))]/10 text-[hsl(var(--${SEV_COLOR[a.severity]}))]`}>{a.severity}</Badge>
                    {a.verified && <Badge className="bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))]">Verified</Badge>}
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight">{a.title}</div>
                  <div className="text-sm text-muted-foreground line-clamp-3">{a.description}</div>
                  <div className="text-xs text-muted-foreground mt-3 flex items-center justify-between">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.latitude?.toFixed(3)}, {a.longitude?.toFixed(3)}</span>
                    <span>{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                <div className="text-xs text-muted-foreground">by {a.reporter_name}</div>
                <Button size="sm" variant="ghost" onClick={() => upvote(a.id)} data-testid={`upvote-${a.id}`}>
                  <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> {a.upvotes || 0}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
